// Section « Disques physiques » de la fiche appareil — type de support et
// S.M.A.R.T.
//
// Séparée de la section des volumes à dessein : un volume dit combien il reste
// d'espace, un disque dit combien il reste de vie. Les deux questions n'ont ni
// les mêmes seuils, ni le même remède, et un même disque porte souvent
// plusieurs volumes.

import { useState } from 'react';
import { HardDrive, AlertTriangle, Info, X, ListTree, ChevronDown, ChevronRight } from 'lucide-react';

// Le type de support est l'information la plus regardée de cette section : il
// décide de ce qu'on peut promettre à un client (« ce poste est encore sur
// disque mécanique »), d'où un badge coloré plutôt qu'une ligne de texte.
const MEDIA_BADGE = {
  nvme: { label: 'SSD NVMe', cls: 'bg-violet-500/20 text-violet-400' },
  ssd: { label: 'SSD', cls: 'bg-blue-500/20 text-blue-400' },
  hdd: { label: 'HDD', cls: 'bg-amber-500/20 text-amber-300' },
  removable: { label: 'Amovible', cls: 'bg-slate-500/20 text-muted' },
  virtual: { label: 'Virtuel', cls: 'bg-slate-500/20 text-muted' },
};

const HEALTH_BADGE = {
  healthy: { label: 'Sain', cls: 'bg-ok/15 text-emerald-300' },
  warning: { label: 'À surveiller', cls: 'bg-warn/15 text-amber-300' },
  unhealthy: { label: 'Défaillant', cls: 'bg-crit/20 text-red-300' },
};

// Le verdict du disque lui-même l'emporte sur celui de l'OS : Windows déclare
// « Sain » un NVMe dont le contrôleur a passé le support en lecture seule, et le
// badge contredisait alors l'avertissement affiché juste dessous.
const FAILURE_BADGE = { label: 'Défaillance annoncée', cls: 'bg-crit/20 text-red-300' };
const healthBadgeOf = (d) => (d.predictFailure ? FAILURE_BADGE : HEALTH_BADGE[d.healthStatus]);

// Au-delà de 100 % (la norme NVMe va jusqu'à 255) : « 238 % de l'endurance
// nominale » se lit comme un bogue d'affichage.
const wearCaption = (p) => (p > 100 ? 'endurance nominale dépassée' : "de l'endurance nominale");

export const mediaBadgeOf = (mediaType) => MEDIA_BADGE[mediaType] || null;

const fmtSize = (mb) => (mb >= 1024 ? Math.round(mb / 1024).toLocaleString('fr-FR') + ' Go' : mb + ' Mo');
const fmtInt = (n) => Number(n).toLocaleString('fr-FR');

// null = non mesuré, 0 = mesuré à zéro : la distinction vient de l'agent et doit
// survivre à l'affichage, sinon un NVMe sans compteur de secteurs afficherait
// « 0 secteur réalloué », ce qui est une affirmation qu'on ne peut pas tenir.
const has = (v) => v !== null && v !== undefined;

const Meter = ({ percent }) => (
  <div className="bg-rule rounded h-1.5 w-16 overflow-hidden">
    <div
      className="h-full rounded"
      style={{
        width: `${Math.min(Math.max(percent, 0), 100)}%`,
        background: percent >= 95 ? '#ef4444' : percent >= 90 ? '#f59e0b' : '#22c55e',
      }}
    />
  </div>
);


// ─── Fiche détaillée d'un disque ─────────────────────────────
// Tout ce que le disque annonce, dans une fenêtre : la section principale reste
// courte (support, santé, usure), la fenêtre porte le reste (norme, lien,
// fonctionnalités, volumes lus et écrits, arrêts brutaux, journal d'erreurs).
// Une valeur absente n'est pas affichée : null = non mesuré, comme partout ici.

const fmtTemp = (c) => `${Math.round(c)} °C`;
const fmtPct = (p) => `${Math.round(p)} %`;

const SOURCE_LABELS = {
  'nvme-health-log': 'Journal de santé NVMe (lecture directe du contrôleur)',
  'storage-reliability-counter': 'Compteurs de fiabilité Windows',
  'failure-predict-data': 'Table S.M.A.R.T. ATA (WMI)',
  smartctl: 'smartctl (smartmontools)',
};

// D'où vient le pourcentage d'usure. « ata:177 (Samsung) » vient du serveur, qui
// choisit l'attribut d'après le fabricant : on le dit, pour qu'un technicien
// puisse juger la valeur à l'aune de sa provenance.
const wearSourceLabel = (src) => {
  if (!src) return null;
  if (src === 'nvme-health-log') return 'journal de santé NVMe';
  if (src === 'storage-reliability-counter') return 'compteurs de fiabilité Windows';
  const m = /^ata:(\d+)(?: \((.+)\))?$/.exec(src);
  if (m) return `attribut S.M.A.R.T. ${m[1]}${m[2] ? `, table ${m[2]}` : ''}`;
  return src;
};

// Noms usuels des attributs ATA, pour l'agent Windows qui ne les connaît pas
// (smartctl les fournit). Liste volontairement courte : ce qui est le même chez
// tous. Un identifiant absent s'affiche nu.
const ATA_NAMES = {
  1: 'Taux d\'erreurs de lecture', 5: 'Secteurs réalloués', 9: 'Heures de fonctionnement',
  12: 'Cycles d\'alimentation', 170: 'Blocs de réserve disponibles', 171: 'Échecs de programmation',
  172: 'Échecs d\'effacement', 173: 'Cycles d\'effacement moyens', 174: 'Coupures imprévues',
  177: 'Nivellement d\'usure', 179: 'Blocs de réserve utilisés', 181: 'Échecs de programmation',
  182: 'Échecs d\'effacement', 183: 'Erreurs de liaison SATA', 184: 'Erreurs de bout en bout',
  187: 'Erreurs non corrigibles signalées', 188: 'Commandes expirées', 190: 'Température (air)',
  194: 'Température', 195: 'Erreurs ECC corrigées', 196: 'Réallocations', 197: 'Secteurs en attente',
  198: 'Secteurs incorrigibles', 199: 'Erreurs CRC UDMA', 202: 'Vie restante (%)',
  230: 'Indicateur d\'usure', 231: 'Vie restante (SSD)', 232: 'Réserve disponible',
  233: 'Indicateur d\'usure', 235: 'Coupures imprévues', 241: 'Total écrit (LBA)', 242: 'Total lu (LBA)',
};

function AtaAttributesTable({ attributes }) {
  const [open, setOpen] = useState(false);
  if (!attributes?.length) return null;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 bg-transparent border-none p-0 cursor-pointer text-[10.5px] uppercase tracking-wider text-muted-2 font-semibold hover:text-fg"
      >
        <Chevron size={12} strokeWidth={2} /> Attributs S.M.A.R.T. ({attributes.length})
      </button>
      {open && (
        <table className="w-full mt-1.5 text-[11px] font-mono">
          <thead>
            <tr className="text-muted-2 text-[10px]">
              <th className="text-left font-normal py-0.5">ID</th>
              <th className="text-left font-normal py-0.5">Attribut</th>
              <th className="text-right font-normal py-0.5">Valeur</th>
              <th className="text-right font-normal py-0.5">Pire</th>
              <th className="text-right font-normal py-0.5">Brut</th>
            </tr>
          </thead>
          <tbody>
            {attributes.map((a) => (
              <tr key={a.id} className="border-t border-rule/60">
                <td className="py-0.5 text-muted">{a.id.toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="py-0.5 text-fg-2 font-sans">{a.name || ATA_NAMES[a.id] || ''}</td>
                <td className="py-0.5 text-right text-fg">{a.value}</td>
                <td className="py-0.5 text-right text-muted">{a.worst}</td>
                <td className="py-0.5 text-right text-muted">{fmtInt(a.raw)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Section({ title, rows }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (shown.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="text-[10.5px] uppercase tracking-wider text-muted-2 font-semibold mb-1.5">{title}</div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1">
        {shown.map(([k, v]) => (
          <div key={k} className="contents">
            <div className="text-[12px] text-muted">{k}</div>
            <div className="text-[12px] text-fg font-mono text-right break-all">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DiskDetailsModal({ disk: d, onClose }) {
  const media = mediaBadgeOf(d.mediaType);
  const health = healthBadgeOf(d);
  const hasPredict = d.predictFailure;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4" onClick={onClose}>
      <div
        className="bg-card border border-rule rounded-2xl w-[520px] max-w-full max-h-[90vh] shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-3 flex-none">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <HardDrive size={18} strokeWidth={1.75} className="text-blue-400 shrink-0" />
              <h2 className="text-base font-bold text-fg truncate">{d.model || d.deviceId || 'Disque'}</h2>
            </div>
            <button onClick={onClose} className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1">
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            {media && <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${media.cls}`}>{media.label}</span>}
            {d.busType && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rule text-muted">{d.busType}</span>}
            {health && <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${health.cls}`}>{health.label}</span>}
            <span className="ml-auto text-[11px] font-mono text-muted">{fmtSize(d.sizeMB)}</span>
          </div>
        </div>

        <div className="px-6 pb-6 overflow-y-auto">
          {hasPredict && (
            <div className="bg-crit/10 border border-crit/30 rounded-lg px-3 py-2 mb-4 flex items-start gap-2">
              <AlertTriangle size={14} strokeWidth={2} className="text-red-300 shrink-0 mt-0.5" />
              <div className="text-[12px] text-red-300 font-semibold">
                Défaillance annoncée par le disque{d.predictFailureReason ? ` : ${d.predictFailureReason}` : '.'}
              </div>
            </div>
          )}

          <Section
            title="Identité"
            rows={[
              ['Numéro de disque', d.deviceId],
              ['Numéro de série', d.serialNumber],
              ['Firmware', d.firmwareVersion],
              ['Norme', d.standard],
              ['Lien', d.linkSpeed],
              ['Vitesse de rotation', d.spindleSpeedRpm > 0 ? `${fmtInt(d.spindleSpeedRpm)} tr/min` : null],
              ['Amovible', d.isRemovable ? 'Oui' : null],
              ['Fonctionnalités', d.features?.length ? d.features.join(', ') : null],
              ['Volumes portés', d.volumes?.length ? d.volumes.join(', ') : null],
            ]}
          />

          <Section
            title="Santé"
            rows={[
              ['Verdict', health ? health.label : d.healthStatus === 'unknown' ? 'Inconnu' : null],
              ['Usure', has(d.wearPercent) ? `${fmtPct(d.wearPercent)} ${wearCaption(d.wearPercent)}` : null],
              ['Source de l\'usure', has(d.wearPercent) ? wearSourceLabel(d.wearSource) : null],
              ['Réserve de blocs', has(d.availableSparePercent)
                ? fmtPct(d.availableSparePercent) + (has(d.availableSpareThresholdPercent) ? ` (seuil ${fmtPct(d.availableSpareThresholdPercent)})` : '')
                : null],
              ['Température', has(d.temperatureC) && d.temperatureC > 0 ? fmtTemp(d.temperatureC) : null],
            ]}
          />

          <Section
            title="Compteurs"
            rows={[
              ["Heures d'allumage", has(d.powerOnHours) ? `${fmtInt(d.powerOnHours)} h` : null],
              ['Démarrages', has(d.powerCycles) ? fmtInt(d.powerCycles) : null],
              ['Arrêts brutaux', has(d.unsafeShutdowns) ? fmtInt(d.unsafeShutdowns) : null],
              ['Total lu par l\'hôte', has(d.totalReadMB) ? fmtSize(d.totalReadMB) : null],
              ['Total écrit par l\'hôte', has(d.totalWrittenMB) ? fmtSize(d.totalWrittenMB) : null],
              ['Secteurs réalloués', has(d.reallocatedSectors) ? fmtInt(d.reallocatedSectors) : null],
              ['Secteurs en attente', has(d.pendingSectors) ? fmtInt(d.pendingSectors) : null],
              ['Erreurs non corrigibles', has(d.uncorrectableErrors) ? fmtInt(d.uncorrectableErrors) : null],
              ["Entrées du journal d'erreurs", has(d.errorLogEntries) ? fmtInt(d.errorLogEntries) : null],
            ]}
          />

          <AtaAttributesTable attributes={d.ataAttributes} />

          <div className="flex items-start gap-1.5 text-[10.5px] text-muted-2 mt-2">
            <Info size={12} strokeWidth={1.75} className="shrink-0 mt-0.5" />
            <span>
              {d.smartAvailable
                ? `Source : ${SOURCE_LABELS[d.smartSource] || d.smartSource || 'inconnue'}. Une ligne absente n'est pas publiée par ce disque.`
                : d.smartUnavailableReason || 'Aucune donnée S.M.A.R.T. pour ce disque.'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PhysicalDisksPanel({ inv, sectionCls, sTitleCls }) {
  const disks = inv?.physicalDisks || [];
  const [details, setDetails] = useState(null);

  // Agent antérieur à la collecte du support : la section disparaît au lieu
  // d'afficher un vide qui ferait croire à une machine sans disque.
  if (disks.length === 0) return null;

  return (
    <div className={sectionCls}>
      <div className={sTitleCls}>
        <HardDrive size={16} strokeWidth={1.75} /> Disques physiques
        <span className="ml-auto text-xs font-mono text-muted-2">
          {disks.length} disque{disks.length > 1 ? 's' : ''}
        </span>
      </div>

      {disks.map((d, i) => {
        const media = mediaBadgeOf(d.mediaType);
        const health = healthBadgeOf(d);
        const stats = [];
        if (has(d.temperatureC) && d.temperatureC > 0) stats.push(`${Math.round(d.temperatureC)} °C`);
        if (has(d.powerOnHours)) stats.push(`${fmtInt(d.powerOnHours)} h d'allumage`);
        if (has(d.powerCycles)) stats.push(`${fmtInt(d.powerCycles)} démarrages`);
        if (has(d.availableSparePercent)) stats.push(`réserve ${Math.round(d.availableSparePercent)} %`);
        if (d.spindleSpeedRpm > 0) stats.push(`${fmtInt(d.spindleSpeedRpm)} tr/min`);

        // Les compteurs de secteurs ne sont montrés qu'au-delà de zéro : à zéro
        // ils n'apprennent rien, au-dessus ils sont l'essentiel.
        const defects = [];
        if (d.reallocatedSectors > 0) defects.push(`${fmtInt(d.reallocatedSectors)} secteur(s) réalloué(s)`);
        if (d.pendingSectors > 0) defects.push(`${fmtInt(d.pendingSectors)} secteur(s) en attente`);
        if (d.uncorrectableErrors > 0) defects.push(`${fmtInt(d.uncorrectableErrors)} erreur(s) non corrigible(s)`);

        return (
          <div
            key={d.serialNumber || d.deviceId || i}
            className={i < disks.length - 1 ? 'mb-4 pb-4 border-b border-sidebar' : ''}
          >
            <div className="flex items-center flex-wrap gap-2 mb-1.5">
              <span className="text-[13px] text-fg font-semibold">{d.model || d.deviceId || 'Disque'}</span>
              {media && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${media.cls}`}>{media.label}</span>
              )}
              {d.busType && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rule text-muted">
                  {d.busType}
                </span>
              )}
              {health && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${health.cls}`}>{health.label}</span>
              )}
              <span className="ml-auto text-[11px] font-mono text-muted">{fmtSize(d.sizeMB)}</span>
            </div>

            <div className="flex items-center gap-2 mb-1.5">
              <div className="text-[10.5px] text-muted-2 font-mono min-w-0 truncate">
                {d.deviceId}
                {d.serialNumber ? ` · ${d.serialNumber}` : ''}
                {d.firmwareVersion ? ` · firmware ${d.firmwareVersion}` : ''}
              </div>
              <button
                onClick={() => setDetails(d)}
                title="Toutes les données du disque"
                className="ml-auto shrink-0 inline-flex items-center gap-1 bg-transparent border border-rule rounded px-1.5 py-0.5 text-[10px] text-muted hover:text-fg hover:border-fg/40 cursor-pointer"
              >
                <ListTree size={11} strokeWidth={1.75} /> Détails
              </button>
            </div>

            {d.predictFailure && (
              <div className="bg-crit/10 border border-crit/30 rounded-lg px-3 py-2 mb-2 flex items-start gap-2">
                <AlertTriangle size={14} strokeWidth={2} className="text-red-300 shrink-0 mt-0.5" />
                <div className="text-[12px] text-red-300 font-semibold">
                  Le disque annonce une défaillance imminente — sauvegarder et remplacer sans attendre.
                </div>
              </div>
            )}

            {has(d.wearPercent) && (
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[11px] text-muted-2">Usure</span>
                <Meter percent={d.wearPercent} />
                <span className="text-[11px] font-mono text-fg-2">{Math.round(d.wearPercent)} %</span>
                <span className="text-[10px] text-muted-3">{wearCaption(d.wearPercent)}</span>
              </div>
            )}

            {stats.length > 0 && <div className="text-[11px] text-muted">{stats.join(' · ')}</div>}

            {defects.length > 0 && (
              <div className="text-[11px] text-amber-300 mt-1">{defects.join(' · ')}</div>
            )}

            {d.volumes?.length > 0 && (
              <div className="text-[11px] text-muted-2 mt-1 font-mono">Porte : {d.volumes.join(', ')}</div>
            )}

            {!d.smartAvailable && d.smartUnavailableReason && (
              <div className="flex items-start gap-1.5 text-[10.5px] text-muted-2 mt-1.5">
                <Info size={12} strokeWidth={1.75} className="shrink-0 mt-0.5" />
                <span>{d.smartUnavailableReason}</span>
              </div>
            )}
          </div>
        );
      })}

      {details && <DiskDetailsModal disk={details} onClose={() => setDetails(null)} />}
    </div>
  );
}
